import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import {
  confirmOffered,
  confirmable,
  getSectionStatus,
  predecessorsConfirmed,
  SECTION_LABEL,
  type GuidedSection,
} from '@lds-efsp/shared-contracts';
import { useEFiling } from '../contexts/EFilingContext';
import { buildSectionReadinessOptions } from '../lib/section-readiness-options';

interface SectionConfirmControlProps {
  brand: BrandConfig;
  section: GuidedSection;
  /** What the filer is approving, in their own terms. */
  description: string;
}

/**
 * One guided section's confirm control (SPEC-SOR-174 R2/R28/R31).
 *
 * This is the ONLY surface that reaches `confirmSection`, so the filer's act is
 * the sole provenance of a `confirmed` status by construction rather than by
 * convention — nothing else in the wizard can produce that value.
 *
 * Whether the control is OFFERED is `confirmOffered`'s decision and not this
 * component's: the readiness half (R2/R31) lives in shared order-logic, and
 * re-deriving "is this ready" here would be a second implementation free to
 * drift from the one the validator's blocker messages read. The component only
 * renders the three states that decision produces — confirmed, offered, and
 * waiting — and each of the two not-offered states says which of the two
 * reasons it is, because "the button is greyed out" with no reason is the dead
 * stall the R76/R77 blocker discipline exists to prevent.
 *
 * The control renders on the step that OWNS the section (AX3); it is mounted by
 * DocumentsTab for the court and case sections and by ConfirmFilingTab for the
 * parties and documents sections, and is confirm-flow gated at both sites (R16
 * — the model is inert in the classic flow).
 */
const SectionConfirmControl: React.FC<SectionConfirmControlProps> = ({
  brand,
  section,
  description,
}) => {
  const { wizardState, confirmSection, docTypeMetadata } = useEFiling();

  const label = SECTION_LABEL[section];
  // R9 — the same court-scoped reference data the validator's section blockers
  // are worded from, through the one builder. Re-deriving it here (or passing
  // only `metadataByDocType`, as this did) lets the offer and the blocker
  // disagree about whether a section is ready.
  const options = React.useMemo(
    () => buildSectionReadinessOptions(docTypeMetadata),
    [docTypeMetadata],
  );
  const status = getSectionStatus(wizardState, section);
  const offered = confirmOffered(wizardState, section, options);
  // Which of the two not-offered reasons applies. Readiness is checked first
  // because an unready section is the actionable one — its fix is on this step.
  const ready = confirmable(wizardState, section, options);
  const waitingOnPredecessor = ready && !predecessorsConfirmed(wizardState, section);

  return (
    <div
      // The blocker panel's jump target for this section's rule — keep in sync
      // with NEXT_BLOCKER_PRESENTATION in EFilingWizard.tsx, which resolves it
      // through getElementById.
      id={`section-confirm-${section}`}
      data-testid={`section-confirm-${section}`}
      data-section-status={status}
      className="mb-6 p-3 rounded-lg border flex items-start justify-between gap-3"
      style={{
        borderColor: status === 'confirmed' ? brand.colors.success || '#059669' : brand.colors.border,
        backgroundColor: brand.colors.bgWhite,
      }}
    >
      <div>
        <p className="text-sm font-semibold" style={{ color: brand.colors.text }}>
          {label}
        </p>
        <p className="text-xs" style={{ color: brand.colors.textLight }}>
          {status === 'confirmed'
            ? `You confirmed the ${label.toLowerCase()} details.`
            : waitingOnPredecessor
              ? `Confirm the sections above before the ${label.toLowerCase()} section.`
              : ready
                ? description
                : `Finish the ${label.toLowerCase()} details above, then confirm.`}
        </p>
      </div>
      {status === 'confirmed' ? (
        <span
          data-testid={`section-confirmed-${section}`}
          className="text-xs font-semibold whitespace-nowrap"
          style={{ color: brand.colors.success || '#059669' }}
        >
          &#10003; Confirmed
        </span>
      ) : (
        <button
          type="button"
          data-testid={`section-confirm-button-${section}`}
          disabled={!offered}
          onClick={() => confirmSection(section)}
          className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors"
          style={{
            backgroundColor: brand.colors.primary,
            opacity: offered ? 1 : 0.5,
            cursor: offered ? 'pointer' : 'not-allowed',
            color: brand.colors.onPrimary,
          }}
        >
          Confirm {label}
        </button>
      )}
    </div>
  );
};

export default SectionConfirmControl;
